'use client';

import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProviders } from "@/providers/AppProviders";
import { ChunkLoadErrorBoundary } from "@/components/ChunkLoadErrorBoundary";
import {
    canStartPullRefresh,
    shouldTriggerPullRefresh,
    type PullRefreshGesture,
} from "@/utils/mobilePullRefresh";
import { isTVDevice } from "@/utils/tvDevice";
const TVApp = React.lazy(() => import("@/tv/TVApp"));

// in App.tsx or routes.tsx

// Pages (lazy loaded)
const LandingPage = React.lazy(() => import("@/pages/LandingPage.tsx"));
// Explicit extensions avoid resolving stale generated .js files that live next
// to the TypeScript sources in this repository.
const Index = React.lazy(() => import("@/pages/Index.tsx"));
const FAQpage = React.lazy(() => import("@/pages/FAQpage"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const YouTubeCallback = React.lazy(() => import("@/pages/auth/youtube/callback"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));
const Checkout = React.lazy(() => import("@/pages/Checkout"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const AboutPage = React.lazy(() => import("@/pages/AboutPage"));
const ServicesPage = React.lazy(() => import("@/pages/ServicesPage"));
const ContactUs = React.lazy(() => import("@/pages/ContactUs"));
const SupportPage = React.lazy(() => import("@/pages/SupportPage"));
const SuperAdminPanel = React.lazy(() => import('@/components/Admin/SuperAdminPanel.tsx'));


// Add this to your lazy imports section:
const SpotifyCallback = React.lazy(() => import("@/pages/auth/spotify/SpotifyCallback")); 
const SoundCloudCallback = React.lazy(() => import("@/pages/auth/soundcloud/callback")); 
const AppleMusicCallback = React.lazy(() => import("@/pages/auth/applemusic/callback")); 
const PaymentSuccess = React.lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = React.lazy(() => import("@/pages/PaymentCancel"));
const PlatformPreview = React.lazy(() => import("@/pages/PlatformPreview"));
const EarningsDetailsPage = React.lazy(() => import("@/components/Header/EarningsDetailsPage"));
const AdminLogin = React.lazy(() => import("@/pages/AdminLogin"));
const AdminControls = React.lazy(() => import("@/pages/AdminControls"));
const PricingPage = React.lazy(() => import("@/pages/PricingPage"));
const TermsAndConditions = React.lazy(() => import("@/pages/TermsAndConditions"));
const JoinSessionPage = React.lazy(() => import("@/pages/JoinSessionPage"));

const CallbackPage = React.lazy(() => import("@/pages/CallbackPage"));


import { ACCOUNT_AUTH_LOSS_REDIRECT_KEY } from "@/utils/authLossRedirect";

// Context Providers
import { useUser } from "@/context/userContext";
import { useGuestUser } from "@/context/GuestUserContext";

// UI

// Route Guards
import PrivateRoute from "@/routes/PrivateRoute";
import GuestOnlyRoute from "@/routes/GuestOnlyRoute";
import AdminOnlyRoute from "@/routes/AdminOnlyRoute";
import { usePlatformSettings } from "@/context/PlatformSettingsContext";

const PageLoadingFallback = () => (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background text-muted-foreground">
        <span className="brand-pill qit-loading-pill qit-loading-pill-page" aria-label="Loading" />
    </div>
);

const HomeRoute = () => {
    const { user, loading } = useUser();
    const { guestUser } = useGuestUser();
    const location = useLocation();
    const accountAuthLost =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem(ACCOUNT_AUTH_LOSS_REDIRECT_KEY) === "1";

    if (loading) {
        return <PageLoadingFallback />;
    }

    if (!user && (!guestUser || accountAuthLost)) {
        return <Navigate to="/" replace state={{ from: location }} />;
    }

    return <Index />;
};

const FeatureUnavailable = ({ title, message }: { title: string; message: string }) => (
    <main className="flex min-h-[100dvh] items-center justify-center bg-background px-4 text-foreground">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-8 text-center shadow-sm">
            <h1 className="text-2xl font-semibold">{title}</h1>
            <p className="mt-3 text-sm text-muted-foreground">{message}</p>
        </div>
    </main>
);

const TVLoadingFallback = () => {
    const [takingTooLong, setTakingTooLong] = useState(false);

    useEffect(() => {
        const timer = window.setTimeout(() => setTakingTooLong(true), 12_000);
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <main className="flex min-h-screen items-center justify-center bg-[#070b0f] px-8 text-white">
            {takingTooLong ? (
                <div className="max-w-xl text-center">
                    <h1 className="text-3xl font-black">QitPlaylist could not finish loading</h1>
                    <p className="mt-3 text-white/65">Check the TV connection, then reload the page.</p>
                    <button type="button" onClick={() => window.location.reload()} className="mt-7 rounded-xl bg-white px-8 py-3 font-bold text-[#070b0f] focus:outline-none focus:ring-4 focus:ring-cyan-300">
                        Reload QitPlaylist
                    </button>
                </div>
            ) : (
                <span className="brand-pill qit-loading-pill qit-loading-pill-page" aria-label="Loading" />
            )}
        </main>
    );
};

const PaymentRoute = ({ children }: { children: React.ReactNode }) => {
    const { settings, loading } = usePlatformSettings();
    if (loading) return <PageLoadingFallback />;
    return settings.paymentsEnabled ? children : <FeatureUnavailable title="Payments temporarily unavailable" message="Purchases are currently paused. Please try again later." />;
};

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
    const { settings, loading } = usePlatformSettings();
    const location = useLocation();
    const adminPath = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
    if (loading) return <PageLoadingFallback />;
    if (settings.maintenanceMode && !adminPath) {
        return <FeatureUnavailable title="QitPlaylist is under maintenance" message={settings.announcementText || "We are making a few improvements. Please check back shortly."} />;
    }
    return children;
};


// -------------------- Main App --------------------
const App: React.FC = () => {
    const isTVRoute =
        typeof window !== "undefined" &&
        /^\/tv(?:\/|$)/.test(window.location.pathname);
    const isTVExperience = isTVRoute || isTVDevice();

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return;
        if (/^\/tv(?:\/|$)/.test(window.location.pathname)) return;

        const isMobileLike = () =>
            window.matchMedia?.("(pointer: coarse)").matches ||
            window.matchMedia?.("(max-width: 768px)").matches;

        const gesture: PullRefreshGesture = {
            startX: 0,
            startY: 0,
            startTime: 0,
            armed: false,
        };
        let lastRefreshAt = 0;

        const handleTouchStart = (event: TouchEvent) => {
            if (!canStartPullRefresh(event, isMobileLike())) {
                gesture.armed = false;
                return;
            }
            const touch = event.touches[0];
            gesture.startX = touch.clientX;
            gesture.startY = touch.clientY;
            gesture.startTime = Date.now();
            gesture.armed = true;
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (!gesture.armed) return;
            if (!shouldTriggerPullRefresh(gesture, event)) return;

            const now = Date.now();
            if (now - lastRefreshAt > 1500) {
                lastRefreshAt = now;
                gesture.armed = false;
                window.location.reload();
            }
        };

        const handleTouchEnd = () => {
            gesture.armed = false;
        };

        document.addEventListener("touchstart", handleTouchStart, { passive: true });
        document.addEventListener("touchmove", handleTouchMove, { passive: true });
        document.addEventListener("touchend", handleTouchEnd, { passive: true });
        document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener("touchstart", handleTouchStart);
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", handleTouchEnd);
            document.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, []);

    // TV mode uses its own minimal provider tree. Global announcements,
    // account prompts, and mobile overlays never cover the ten-foot display.
    if (isTVExperience) {
        return (
            <ChunkLoadErrorBoundary>
                <Suspense fallback={<TVLoadingFallback />}>
                    <TVApp />
                </Suspense>
            </ChunkLoadErrorBoundary>
        );
    }

    return (
        <AppProviders>
            <ChunkLoadErrorBoundary>
                <BrowserRouter>
                    <Suspense
                        fallback={<PageLoadingFallback />}
                    >
                        <MaintenanceGate>
                        <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        {/* Legacy auth aliases used by older links/builds */}
                        <Route path="/login" element={<LandingPage />} />
                        <Route path="/signin" element={<LandingPage />} />
                        <Route path="/faq" element={<FAQpage />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        <Route path="/checkout" element={<PaymentRoute><Checkout /></PaymentRoute>} />
                        <Route path="/contact" element={<ContactUs />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/home" element={<HomeRoute />} />
                        <Route path="/platform-preview" element={<PlatformPreview />} />
                        <Route path="/admin" element={<AdminLogin />} />

                        <Route path="/callback" element={<CallbackPage />} />
                        <Route path="/youtube/callback" element={<YouTubeCallback />} />

                        {/* Guest Only Routes */}
                        <Route element={<GuestOnlyRoute />}>
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                        </Route>

                        {/* Protected (logged-in) */}
                        <Route element={<PrivateRoute />}>
                            {/* Add private-only routes here */}
                            <Route path="/earnings-details" element={<EarningsDetailsPage />} />
                        </Route>

                        {/* Admin Only */}
                        <Route element={<AdminOnlyRoute />}>
                            <Route path="/admin/dashboard" element={<SuperAdminPanel />} />
                            <Route path="/admin/controls" element={<AdminControls />} />
                        </Route>

                        {/* Session routes */}
                        <Route path="/join-session" element={<JoinSessionPage />} />
                        <Route path="/join-session/:sessionId" element={<JoinSessionPage />} />
                        <Route path="/join/:sessionId" element={<JoinSessionPage />} />
                        <Route path="/session/:sessionId" element={<JoinSessionPage />} />

                        <Route path="/payment-success" element={<PaymentRoute><PaymentSuccess /></PaymentRoute>} />
                        <Route path="/payment-cancel" element={<PaymentRoute><PaymentCancel /></PaymentRoute>} /> {/* Optional */}

                        {/* 404 Fallback */}
                        <Route path="*" element={<NotFound />} />

                        {/* Auth Callbacks - Add these */}
                        <Route path="/auth/spotify/callback" element={<SpotifyCallback />} /> 
                        <Route path="/auth/soundcloud/callback" element={<SoundCloudCallback />} />
                        <Route path="/auth/applemusic/callback" element={<AppleMusicCallback />} />
                        <Route path="/auth/youtube/callback" element={<YouTubeCallback />} />

                        <Route path="/pricing" element={<PricingPage />} />


                        </Routes>
                        </MaintenanceGate>
                    </Suspense>
                </BrowserRouter>
            </ChunkLoadErrorBoundary>
        </AppProviders>
    );
};

export default App;
